import React from 'react';
import { CategoryStats, ComplianceChecklist, MAPPING_DATA } from '../data/complianceData';

interface ComplianceStats {
  total: number;
  completed: number;
  inProgress: number;
  notStarted: number;
  completionRate: number;
}

interface Props {
  complianceStats: ComplianceStats;
  categoryStats: CategoryStats;
  checklist: ComplianceChecklist;
}

interface CircleProps {
  completed: number;
  total: number;
  label: string;
  sublabel: string;
}

const FrameworkCircle: React.FC<CircleProps> = ({ completed, total, label, sublabel }) => {
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
  const radius = 42;
  const circumference = 2 * Math.PI * radius; // 263.89
  const offset = circumference - (circumference * percentage) / 100;

  return (
    <div className="text-center">
      <div className="relative w-24 h-24 mx-auto">
        <svg className="transform -rotate-90 w-24 h-24" viewBox="0 0 96 96">
          <circle cx="48" cy="48" r={radius} stroke="#e5e7eb" strokeWidth="12" fill="none" />
          <circle
            cx="48" cy="48" r={radius}
            stroke={percentage === 100 ? '#16a34a' : percentage > 0 ? '#3b82f6' : '#d1d5db'}
            strokeWidth="12" fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.6s ease' }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-lg font-bold ${percentage === 100 ? 'text-green-600' : 'text-blue-600'}`}>
            {percentage}%
          </span>
        </div>
      </div>
      <h3 className="mt-3 font-medium text-gray-700 text-sm">{label}</h3>
      <p className="text-xs text-gray-400 mt-0.5">{completed}/{total} done</p>
      <p className="text-xs text-gray-400">{sublabel}</p>
    </div>
  );
};

const VisualizationView: React.FC<Props> = ({ complianceStats, categoryStats, checklist }) => {
  // Compute per-framework completion from real checklist data.
  // "total" = requirements that have a non-trivial mapping for that framework (not '-').
  // "completed" = of those, how many the user has marked complete.
  const frameworks = [
    {
      key: 'iso27001' as const,
      label: 'ISO 27001',
      sublabel: '2022 edition',
      filter: (d: typeof MAPPING_DATA[0]) => d.iso27001 !== '-',
    },
    {
      key: 'nist' as const,
      label: 'NIST CSF',
      sublabel: 'v2.0',
      filter: (d: typeof MAPPING_DATA[0]) => d.nist !== '-',
    },
    {
      key: 'etsi' as const,
      label: 'ETSI',
      sublabel: 'EN 319 401',
      filter: (d: typeof MAPPING_DATA[0]) => d.etsi !== '-',
    },
    {
      key: 'cen' as const,
      label: 'CEN',
      sublabel: 'TS 18026:2024',
      filter: (d: typeof MAPPING_DATA[0]) => d.cen !== '-',
    },
  ];

  const frameworkStats = frameworks.map(({ label, sublabel, filter }) => {
    const mapped = MAPPING_DATA.filter(filter);
    const completed = mapped.filter(d => checklist[d.id] === 'completed').length;
    return { label, sublabel, total: mapped.length, completed };
  });

  // National frameworks: all 5 countries, any requirement with a non-empty national ref
  const nationalMapped = MAPPING_DATA.filter(d =>
    Object.values(d.national).some(v => v.trim().length > 0)
  );
  const nationalCompleted = nationalMapped.filter(d => checklist[d.id] === 'completed').length;

  return (
    <div className="grid gap-6">
      {/* Compliance Progress */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-6">Compliance Progress Overview</h2>

        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-medium text-gray-700">Overall Completion</h3>
            <span className="text-2xl font-bold text-blue-600">{complianceStats.completionRate}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-4">
            <div
              className="bg-blue-600 h-4 rounded-full transition-all"
              style={{ width: `${complianceStats.completionRate}%` }}
            />
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Based on {complianceStats.total} NIS2 Article 21 requirements
          </p>
          <div className="grid grid-cols-3 gap-4 mt-4">
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{complianceStats.completed}</p>
              <p className="text-sm text-gray-600">Completed</p>
            </div>
            <div className="text-center p-3 bg-yellow-50 rounded-lg">
              <p className="text-2xl font-bold text-yellow-600">{complianceStats.inProgress}</p>
              <p className="text-sm text-gray-600">In Progress</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-600">{complianceStats.notStarted}</p>
              <p className="text-sm text-gray-600">Not Started</p>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-medium text-gray-700 mb-4">Progress by Category</h3>
          <div className="space-y-3">
            {Object.entries(categoryStats).map(([category, stats]) => (
              <div key={category}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">{category}</span>
                  <span className="text-sm text-gray-500">
                    {stats.completed}/{stats.total} — {stats.percentage}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      stats.percentage === 100 ? 'bg-green-600' : stats.percentage > 0 ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                    style={{ width: `${stats.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Framework Coverage — driven by real checklist data */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-start justify-between mb-2">
          <h2 className="text-xl font-bold text-gray-800">Your Compliance by Framework</h2>
        </div>
        <p className="text-sm text-gray-500 mb-6">
          Percentage of NIS2 requirements — that reference each framework — marked as completed by you.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
          {frameworkStats.map(({ label, sublabel, total, completed }) => (
            <FrameworkCircle key={label} label={label} sublabel={sublabel} total={total} completed={completed} />
          ))}
          <FrameworkCircle
            label="National"
            sublabel="BE/FI/EL/ES/FR"
            total={nationalMapped.length}
            completed={nationalCompleted}
          />
        </div>
      </div>
    </div>
  );
};

export default VisualizationView;
